import React, { useState } from "react";
import styled from "styled-components";

import {
  BONFIRE_USER_ROLES,
  filterMatches,
  formatUserName,
  getAcl,
  hasTag,
  isUserAdministrator,
  isUserRole,
  parseAwsJson,
  SystemTag,
} from "@examspark/common";
import {
  useModal,
  useMutationUpdateWorkspaceUser,
  useQueryListWorkspaceUsers,
  useAppContext,
} from "@examspark/common-app";
import {
  Avatar,
  Button,
  CheckboxButton,
  Grid,
  Modal,
  RouterLink,
  SelectInput,
  SubmitButtons,
  TextInput,
  Toolbar,
  VSpace,
} from "@examspark/common-ui";
import {
  IconAddLine,
  IconGearLine,
  IconRefreshLine,
  IconStarSolid,
} from "@examspark/common-ui/dist/components/icons";
import { Workspace, WorkspaceAcl, WorkspaceUserRole } from "@examspark/types";

import awsconfig from "../../aws-exports";
import Loading from "../common/Loading";

import WorkspaceDomainsModal from "./WorkspaceDomainsModal";
import WorkspaceRegistrationCodesModal from "./WorkspaceRegistrationCodes";

const UserCard = styled.div`
  display: flex;
  align-items: center;
  border: solid 1px #ddd;
  padding: 1rem;
  border-radius: 0.5rem;
  background-color: #f7f7f7;
  display: flex;
  gap: 1rem;
`;

const UserCardRole = styled.div`
  color: #777;
  text-align: right;
`;
const UserCardImage = styled.div``;
const UserCardMeta = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

const UserName = styled.div`
  font-size: 1.7rem;
  line-height: 1.3em;

  svg {
    margin-left: 0.5rem;
  }
`;
const UserTitle = styled.div`
  font-size: 1.1rem;
  color: #777;
`;

const WorkspaceUsers = () => {
  const [state] = useAppContext();
  const [editRoleModalUser, setEditRoleModalUser] = useState<Workspace>();
  const [showDisabled, setShowDisabled] = useState(false);
  const regCodesModal = useModal();
  const domainSettingsModal = useModal();
  const updateWorkspaceUser = useMutationUpdateWorkspaceUser();
  const [isUserSaving, setIsUserSaving] = useState(false);
  const [filter, setFilter] = useState("");
  const { data, isLoading, isFetching, refetch } = useQueryListWorkspaceUsers(
    state.workspaceUser?.workspace?.id,
  );

  const getEditUserRole = (user: Workspace | undefined) => {
    const acl = parseAwsJson<WorkspaceAcl>(user?.acl);
    return acl?.roleIds?.[0] ?? WorkspaceUserRole.Standard;
  };

  const isSessionUserWorkspaceAdministrator = isUserAdministrator(
    state.workspaceUser,
  );

  return (
    <>
      {isSessionUserWorkspaceAdministrator && (
        <>
          <WorkspaceRegistrationCodesModal {...regCodesModal.modalProps} />
          <WorkspaceDomainsModal {...domainSettingsModal.modalProps} />
        </>
      )}
      <Modal
        title={`Edit ${formatUserName(editRoleModalUser?.user)}`}
        isOpen={!!editRoleModalUser}
        onRequestClose={() => setEditRoleModalUser(undefined)}
        buttons={
          <SubmitButtons
            submitLabel="Save"
            onCancel={() => setEditRoleModalUser(undefined)}
            isLoading={isUserSaving}
            onSubmit={async () => {
              if (!state.workspaceUser?.workspace?.id) return;
              if (!editRoleModalUser?.user?.id) return;

              setIsUserSaving(true);
              await updateWorkspaceUser.mutateAsync({
                workspaceId: state.workspaceUser?.workspace?.id,
                userId: editRoleModalUser?.user?.id,
                data: {
                  acl: JSON.stringify(editRoleModalUser.acl),
                },
              });

              setIsUserSaving(false);
              setEditRoleModalUser(undefined);
              refetch();
            }}
          />
        }
      >
        <Modal.PaddedBody>
          <VSpace>
            <SelectInput
              placeholder="Select Role"
              value={getEditUserRole(editRoleModalUser)}
              onChange={(value) => {
                if (editRoleModalUser == null) return;

                setEditRoleModalUser({
                  ...editRoleModalUser,
                  acl: {
                    ...parseAwsJson<WorkspaceAcl>(editRoleModalUser?.acl),
                    roleIds: [value?.value],
                  },
                });
              }}
              options={Object.keys(BONFIRE_USER_ROLES).map((v) => {
                return {
                  label:
                    BONFIRE_USER_ROLES[v as keyof typeof BONFIRE_USER_ROLES]
                      ?.label,
                  value: v as WorkspaceUserRole,
                };
              })}
            />
            <Button
              stretch
              title={
                editRoleModalUser?.isDisabled ? "Enable User" : "Disable User"
              }
              isLoading={isUserSaving}
              onClick={async () => {
                if (!state.workspaceUser?.workspace?.id) return;
                if (!editRoleModalUser?.user?.id) return;

                setIsUserSaving(true);
                await updateWorkspaceUser.mutateAsync({
                  workspaceId: state.workspaceUser?.workspace?.id,
                  userId: editRoleModalUser?.user?.id,
                  data: {
                    isDisabled: !editRoleModalUser?.isDisabled,
                  },
                });

                setIsUserSaving(false);
                setEditRoleModalUser({
                  ...editRoleModalUser,
                  isDisabled: !editRoleModalUser?.isDisabled,
                });
                setEditRoleModalUser(undefined);
                refetch();
              }}
            >
              {editRoleModalUser?.isDisabled ? "Enable User" : "Disable User"}
            </Button>
          </VSpace>
        </Modal.PaddedBody>
      </Modal>
      <VSpace size="2rem">
        <Toolbar>
          <TextInput
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{ flex: "1" }}
            placeholder="Filter users by name or email..."
          />

          <CheckboxButton
            title="show disabled users"
            checked={showDisabled}
            onCheckedChange={setShowDisabled}
          >
            Show disabled users
          </CheckboxButton>
          <Button
            title="Refresh"
            icon={<IconRefreshLine />}
            isLoading={isFetching}
            onClick={() => refetch()}
          >
            Refresh
          </Button>
          {isSessionUserWorkspaceAdministrator && (
            <>
              <Button
                title="Settings"
                onClick={() => domainSettingsModal.setIsOpen(true)}
              >
                <IconGearLine width="16" />
              </Button>
              <Button
                title="Invite Users"
                icon={<IconAddLine />}
                onClick={() => regCodesModal.setIsOpen(true)}
                color="create"
              >
                Invite Users
              </Button>
            </>
          )}
        </Toolbar>
        {isLoading && <Loading>Loading Users...</Loading>}
        {!isLoading && (
          <div>
            <Grid size="56rem" gap="2rem">
              {data?.map((user) => {
                const isUserInherited = hasTag(user, SystemTag.InheritedFrom);

                if (user.isDisabled && !showDisabled) return null;

                if (
                  !filterMatches(
                    filter,
                    user.user?.firstName,
                    user.user?.lastName,
                    user.user?.email,
                  )
                ) {
                  return null;
                }

                const acl = parseAwsJson<WorkspaceAcl>(user.acl);

                const isUserNonBonfireUserRole =
                  acl?.roleIds?.length &&
                  !isUserRole(
                    acl,
                    ...(Object.keys(BONFIRE_USER_ROLES) as WorkspaceUserRole[]),
                  );

                const canModify =
                  user.user?.id !== state.user?.id &&
                  isSessionUserWorkspaceAdministrator &&
                  !isUserNonBonfireUserRole &&
                  !isUserInherited;

                return (
                  <UserCard key={user.user?.id}>
                    <UserCardImage>
                      <Avatar
                        user={user.user}
                        size={48}
                        awsconfig={awsconfig}
                      />
                    </UserCardImage>
                    <UserCardMeta>
                      <div>
                        <div>
                          <UserName>
                            <RouterLink
                              to={`/user/${user.id}/${user.user?.id}`}
                            >
                              {formatUserName(user.user) ?? "(Unnamed User)"}
                            </RouterLink>
                            {!!user.isDisabled && <span> (Disabled)</span>}
                            {isUserRole(
                              user,
                              WorkspaceUserRole.Administrator,
                            ) && <IconStarSolid height="12" fill="#888" />}
                          </UserName>
                        </div>
                        <UserTitle>{user.user?.email}</UserTitle>
                      </div>
                    </UserCardMeta>
                    <UserCardRole>{getAcl(user)?.roleIds?.[0]}</UserCardRole>
                    <div>
                      <Button
                        title={
                          isUserInherited
                            ? "This user is inherited."
                            : isUserNonBonfireUserRole
                              ? "This user's role cannot be changed."
                              : "Settings"
                        }
                        icon={<IconGearLine />}
                        variant="plain"
                        disabled={!canModify}
                        onClick={() => {
                          console.log("user", JSON.stringify(user, null, 2));
                          setEditRoleModalUser(user);
                        }}
                      />
                    </div>
                  </UserCard>
                );
              })}
            </Grid>
          </div>
        )}
      </VSpace>
    </>
  );
};

export default WorkspaceUsers;
